import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Grid,
  IconButton,
  CssBaseline,
  InputAdornment,
  Fade,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Publish as PublishIcon,
  Block as UnpublishedIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Search as SearchIcon,
  EventNote as EventNoteIcon
} from '@mui/icons-material';
import { styled, ThemeProvider } from '@mui/material/styles';
import axiosInstance from '../../api/axiosConfig';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Lottie from 'lottie-react';

import adminTheme from '../../theme';
import PageContainer from '../common/PageContainer';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import animationData from '../../assets/icons/no-results.json';

// --- Constants ---
const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MAX_PERIODS = 8; 

// --- Styled Components ---

const StyledPeriodCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'center',
  cursor: 'pointer',
  position: 'relative',
  padding: theme.spacing(0.5),
  border: `1px solid ${theme.palette.divider}`,
  minWidth: 120,
  height: 70,
  verticalAlign: 'middle',
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.light}`,
  },
}));

const FadeTransition = React.forwardRef(function FadeTransition(props, ref) {
  return <Fade ref={ref} {...props} />;
});

// Skeleton Loader
const TableSkeleton = ({ rows = 5 }) => (
  <AdminPanelCard
    title="Timetable Management"
    subtitle="Preparing class sections, timetable status, and publish controls."
  >
    <Stack spacing={2}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: 1.25,
        }}
      >
        {[...Array(3)].map((_, index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              p: 1.4,
              bgcolor: '#FBFCFF',
            }}
          >
            <Skeleton variant="text" width="50%" height={22} />
            <Skeleton variant="text" width="82%" height={16} />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1.5,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.1fr 1fr 1fr 1fr 1.1fr',
            bgcolor: '#F5F7FB',
            px: 1.5,
            py: 1.1,
            gap: 1.5,
          }}
        >
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} variant="text" width="78%" height={18} />
          ))}
        </Box>

        <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
          {[...Array(rows)].map((_, rowIndex) => (
            <Box
              key={rowIndex}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.1fr 1fr 1fr 1fr 1.1fr',
                alignItems: 'center',
                px: 1.5,
                py: 1.2,
                gap: 1.5,
              }}
            >
              <Skeleton variant="text" width="62%" height={20} />
              <Skeleton variant="rounded" width={72} height={24} />
              <Skeleton variant="rounded" width={92} height={24} />
              <Skeleton variant="rounded" width={86} height={24} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.75 }}>
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    </Stack>
  </AdminPanelCard>
);

// --- Component ---
const TimetableComponent = () => {
  const schoolId = localStorage.getItem('schoolId') || '';

  // --- States ---
  const [initialLoading, setInitialLoading] = useState(true);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [publishingMap, setPublishingMap] = useState({});

  const [subjectsData, setSubjectsData] = useState([]); 
  const [assignments, setAssignments] = useState([]); 
  const [timetables, setTimetables] = useState({}); 
  const [classSectionList, setClassSectionList] = useState([]); 

  const [selectedClass, setSelectedClass] = useState(''); 
  const [selectedSection, setSelectedSection] = useState(''); 
  const [selectedClassName, setSelectedClassName] = useState('');
  const [selectedSectionName, setSelectedSectionName] = useState('');

  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [currentEdit, setCurrentEdit] = useState({ dayOfWeek: '', period: null });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = useState('');

  const [timetableDialogOpen, setTimetableDialogOpen] = useState(false);
  const timetableRef = useRef(null);

  // --- Helpers ---
  const showSnackbar = (message, severity = 'info') => setSnackbar({ open: true, message, severity });

  const formatTimeToAMPM = (time) => {
    if (!time || typeof time !== 'string' || !time.includes(':')) return 'N/A';
    try {
        const parts = time.split(':');
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        if (isNaN(hours) || isNaN(minutes)) return 'Invalid Time';
        const period = hours >= 12 ? 'PM' : 'AM';
        const formattedHours = ((hours + 11) % 12) + 1;
        return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (e) {
        return 'Invalid Time';
    }
  };

  const updateClassSectionListStatus = (classId, sectionId, hasTimetable, status) => {
     setClassSectionList(prevList =>
        prevList.map(item =>
          item.classId === Number(classId) && item.sectionId === Number(sectionId)
            ? { ...item, hasTimetable, timetableStatus: status }
            : item
        )
     );
  };

  // --- Initial Fetch ---
  useEffect(() => {
    let isMounted = true;
    if (!schoolId) {
      setError('School ID not found. Please ensure you are logged in correctly.');
      setInitialLoading(false);
      return;
    }

    const loadInitialData = async () => {
      setInitialLoading(true);
      setError('');
      try {
        const [classesRes, sectionsRes, subjectsRes, timetablesRes] = await Promise.all([
          axiosInstance.get(`/api/classes?schoolId=${schoolId}`),
          axiosInstance.get(`/api/sections?schoolId=${schoolId}`),
          axiosInstance.get(`/api/subjects?schoolId=${schoolId}`), 
          axiosInstance.get('/api/timetables', { headers: { schoolId } }), 
        ]);

        if (!isMounted) return;

        const fetchedSubjects = Array.isArray(subjectsRes.data) ? subjectsRes.data : [];
        setSubjectsData(fetchedSubjects);

        const activeClasses = classesRes.data
          .filter((cls) => cls.status === 'Active')
          .map((cls) => ({
            ...cls,
            assignedSections: cls.assignedSections
              ? String(cls.assignedSections).split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id))
              : [],
          }));
        const activeSections = sectionsRes.data.filter((sec) => sec.status === 'Active');


        const fetchedTimetables = timetablesRes.data || [];
        const timetableStatusMap = {};
        const timetableExistenceMap = {};

        fetchedTimetables.forEach((t) => {
          if (t.classId && t.sectionId) { 
            const key = `${t.classId}-${t.sectionId}`;
            timetableExistenceMap[key] = true;
            const currentEntryStatus = t.status || 'SAVED'; 
            if (!timetableStatusMap[key] || timetableStatusMap[key] === 'PUBLISHED') {
              timetableStatusMap[key] = currentEntryStatus;
            }
          }
        });

        const list = [];
        activeClasses.forEach((cls) => {
          cls.assignedSections.forEach((secId) => {
            const section = activeSections.find((s) => s.sectionId === secId);
            if (section) {
              const key = `${cls.classId}-${secId}`;
              list.push({
                classId: cls.classId,
                className: cls.className,
                sectionId: secId,
                sectionName: section.sectionName,
                hasTimetable: !!timetableExistenceMap[key],
                timetableStatus: timetableStatusMap[key] || 'N/A',
              });
            }
          });
        });

        setClassSectionList(list);
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Failed to load essential data. Please check network connection or contact support.');
      } finally {
        if (isMounted) setInitialLoading(false);
      }
    };

    loadInitialData();
    return () => { isMounted = false; };
  }, [schoolId]);

  // --- Dynamic Fetches ---
  const fetchTimetableDetails = useCallback(async (classId, sectionId) => {
    if (!classId || !sectionId) return;
    const classIdNum = Number(classId);
    const sectionIdNum = Number(sectionId);

    try {
      const response = await axiosInstance.get('/api/timetables', {
        headers: { schoolId },
        params: { classId: classIdNum, sectionId: sectionIdNum },
      });
      const fetchedEntries = response.data || []; 
      const organizedTimetable = {};

      fetchedEntries.forEach((entry) => {
        const { dayOfWeek, period, subjectId, subjectName, startTime, endTime, status, timetableId, meetLink } = entry;
        if (!dayOfWeek || !period || !subjectId || !timetableId) return;

        if (!organizedTimetable[dayOfWeek]) organizedTimetable[dayOfWeek] = Array(MAX_PERIODS).fill(null).map(() => ({}));
        while (organizedTimetable[dayOfWeek].length < period) organizedTimetable[dayOfWeek].push({});

         const subjectInfo = Array.isArray(subjectsData) ? subjectsData.find(sub => sub.subjectId === subjectId) : null;
         const finalSubjectName = subjectName || subjectInfo?.subjectName || 'Unknown Subject';

         organizedTimetable[dayOfWeek][period - 1] = {
            timetableId,
            subjectId,
            subjectName: finalSubjectName,
            startTime,
            endTime,
            status: status || 'SAVED',
            meetLink: meetLink || '',
         };
      });

      daysOfWeek.forEach(day => {
         if (!organizedTimetable[day]) {
             organizedTimetable[day] = Array(MAX_PERIODS).fill(null).map(() => ({}));
         } else {
             while (organizedTimetable[day].length < MAX_PERIODS) organizedTimetable[day].push({});
         }
      });

      setTimetables(prev => ({ ...prev, [classIdNum]: { ...(prev[classIdNum] || {}), [sectionIdNum]: organizedTimetable } }));

      let overallStatus = 'N/A';
      if (fetchedEntries.length > 0) overallStatus = fetchedEntries.some(e => (e.status || 'SAVED') === 'SAVED') ? 'SAVED' : 'PUBLISHED';
      updateClassSectionListStatus(classIdNum, sectionIdNum, fetchedEntries.length > 0, overallStatus);

    } catch (err) {
      showSnackbar('Failed to load timetable details.', 'error');
    }
  }, [schoolId, subjectsData]); 

  const fetchAssignmentsForSelection = useCallback(async (classId, sectionId) => {
      if (!classId || !sectionId) return setAssignments([]);
      setAssignmentLoading(true);
      try {
        const response = await axiosInstance.get(`/api/class-subject-assignments/class/${Number(classId)}/section/${Number(sectionId)}?schoolId=${schoolId}`);
        const fetchedAssignments = response.data || []; 

        const assignmentsWithNames = fetchedAssignments.map(assign => {
          let finalSubjectName = assign.subjectName;
          if (!finalSubjectName && assign.subjectId) {
            const subjectInfo = Array.isArray(subjectsData) ? subjectsData.find(sub => sub.subjectId === assign.subjectId) : null;
            finalSubjectName = subjectInfo?.subjectName; 
          }
          return { ...assign, subjectName: finalSubjectName || 'Unknown Subject' };
        });
        setAssignments(assignmentsWithNames);
      } catch (err) {
        showSnackbar(`Failed to load subject list for section.`, 'error');
        setAssignments([]); 
      } finally {
        setAssignmentLoading(false);
      }
  }, [schoolId, subjectsData]);

  // --- Handlers ---
  const handleViewEditTimetable = async (classId, sectionId, className, sectionName) => {
    setSelectedClass(Number(classId));
    setSelectedSection(Number(sectionId));
    setSelectedClassName(className);
    setSelectedSectionName(sectionName);
    setTimetableDialogOpen(true);
    setEditing(false);
    setCurrentEdit({ dayOfWeek: '', period: null });
    setDialogLoading(true); 

    try {
        await fetchTimetableDetails(classId, sectionId);
        await fetchAssignmentsForSelection(classId, sectionId);
    } catch (err) {
        setTimetableDialogOpen(false); 
    } finally {
        setDialogLoading(false); 
    }
  };

  const handleToggleStatus = async (classId, sectionId) => {
    const classIdNum = Number(classId);
    const sectionIdNum = Number(sectionId);
    const key = `${classIdNum}-${sectionIdNum}`;
    const currentItem = classSectionList.find(item => item.classId === classIdNum && item.sectionId === sectionIdNum);

    const currentStatus = (currentItem?.hasTimetable && currentItem?.timetableStatus !== 'N/A') ? currentItem.timetableStatus : 'SAVED'; 
    const newStatus = currentStatus === 'SAVED' ? 'PUBLISHED' : 'SAVED';

    setPublishingMap((prev) => ({ ...prev, [key]: true }));

    try {
      const response = await axiosInstance.get('/api/timetables', {
        headers: { schoolId },
        params: { classId: classIdNum, sectionId: sectionIdNum },
      });
      const specificTimetables = response.data || []; 

      if (specificTimetables.length === 0) {
         showSnackbar('No timetable entries found to update status.', 'warning');
         setPublishingMap((prev) => ({ ...prev, [key]: false }));
         return;
      }

      const updatePromises = specificTimetables.filter(t => t.timetableId).map(t =>
            axiosInstance.put(`/api/timetables/${t.timetableId}`, { status: newStatus }, { headers: { schoolId } }).catch(err => console.error(err))
      );

      const results = await Promise.allSettled(updatePromises);
      const failedUpdates = results.filter(r => r.status === 'rejected');

      if (failedUpdates.length > 0) {
          showSnackbar(`Partially updated status. ${failedUpdates.length} entries failed.`, 'warning');
          await fetchTimetableDetails(classIdNum, sectionIdNum);
      } else {
          showSnackbar(`Timetable ${newStatus.toLowerCase()} successfully!`, 'success');
          updateClassSectionListStatus(classIdNum, sectionIdNum, true, newStatus);
      }
    } catch (err) {
      showSnackbar(`Failed to retrieve timetable entries to toggle status.`, 'error');
    } finally {
      setPublishingMap((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleExportPDF = () => {
    const input = timetableRef.current;
    if (!input) return showSnackbar('Timetable content not found for export.', 'error');

    showSnackbar('Generating PDF...', 'info');
    html2canvas(input, { scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff' })
      .then((canvas) => {
        const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const margin = 30;
        const imgWidth = pdf.internal.pageSize.getWidth() - 2 * margin;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let finalHeight = imgHeight > pdf.internal.pageSize.getHeight() - 2 * margin ? pdf.internal.pageSize.getHeight() - 2 * margin : imgHeight;
        
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', margin, margin, imgWidth, finalHeight);
        pdf.setFontSize(9);
        pdf.setTextColor(100);
        pdf.text(`Timetable - ${selectedClassName} / ${selectedSectionName}`, margin, margin - 10);
        pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pdf.internal.pageSize.getWidth() - margin - 100, margin - 10);

        pdf.save(`Timetable_${selectedClassName}_${selectedSectionName}.pdf`);
        showSnackbar('PDF exported successfully!', 'success');
      })
      .catch((err) => showSnackbar('Failed to export PDF.', 'error'));
  };

  const handleCellClick = async (dayOfWeek, periodIndex) => {
    if (!assignments || assignments.length === 0 || assignments[0]?.classId !== selectedClass || assignments[0]?.sectionId !== selectedSection) {
       await fetchAssignmentsForSelection(selectedClass, selectedSection); 
    }
    setCurrentEdit({ dayOfWeek, period: periodIndex });
    setEditing(true); 
  };

  const handleSaveTimetable = async () => {
    const { dayOfWeek, period } = currentEdit;
    const entry = timetables[selectedClass]?.[selectedSection]?.[dayOfWeek]?.[period];

    if (!entry?.subjectId) return showSnackbar('Please select a subject.', 'warning');
    if (!entry?.startTime || !entry?.endTime) return showSnackbar('Please enter both start and end times.', 'warning');
    if (entry.startTime >= entry.endTime) return showSnackbar('Start time must be before end time.', 'warning');

    setIsSaving(true);
    const payload = {
        schoolId: Number(schoolId),
        classId: Number(selectedClass),
        sectionId: Number(selectedSection),
        subjectId: Number(entry.subjectId),
        dayOfWeek,
        period: period + 1, 
        startTime: entry.startTime,
        endTime: entry.endTime,
        status: 'SAVED',
        meetLink: entry.meetLink || null,
    };

    try {
      if (entry.timetableId) {
        await axiosInstance.put(`/api/timetables/${entry.timetableId}`, payload, { headers: { schoolId } });
      } else {
        const response = await axiosInstance.post('/api/timetables', payload, { headers: { schoolId } });
        if (response.data?.timetableId) {
           setTimetables(prev => {
               const newData = JSON.parse(JSON.stringify(prev));
               newData[selectedClass][selectedSection][dayOfWeek][period].timetableId = response.data.timetableId;
               return newData;
           });
        }
      }
      showSnackbar('Timetable entry saved!', 'success');
      updateClassSectionListStatus(selectedClass, selectedSection, true, 'SAVED'); 
      setEditing(false); 
    } catch (err) {
      showSnackbar(`Failed to save entry.`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // --- Filtering & Pagination ---
  const filteredList = useMemo(() => {
    return classSectionList.filter(item => 
        item.className.toLowerCase().includes(searchQuery.toLowerCase()) || 
        item.sectionName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [classSectionList, searchQuery]);

  const timetableMetrics = useMemo(() => {
    const readyCount = classSectionList.filter((item) => item.hasTimetable).length;
    const publishedCount = classSectionList.filter((item) => item.timetableStatus === 'PUBLISHED').length;
    return [
      { label: 'Class Sections', value: classSectionList.length },
      { label: 'Ready', value: readyCount },
      { label: 'Published', value: publishedCount },
    ];
  }, [classSectionList]);

  // --- Render Functions ---

  const renderClassesSections = () => {
    if (classSectionList.length === 0 && !initialLoading) {
        return (
            <AdminPanelCard>
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <Box width={150} height={150} mb={2} opacity={0.8}>
                    {animationData ? <Lottie animationData={animationData} loop autoplay /> : <EventNoteIcon sx={{ fontSize: 80, color: 'text.disabled' }} />}
                </Box>
                <Typography variant="h6" color="text.primary" fontWeight="bold">No Timetables Available</Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>Please ensure you have set up Classes, Sections, and Assigned Subjects first.</Typography>
            </Box>
            </AdminPanelCard>
        );
    }

    const paginatedGroups = [];
    let currentClassId = null;
    
    filteredList.forEach(section => {
        if (section.classId !== currentClassId) {
            currentClassId = section.classId;
            paginatedGroups.push({ classId: section.classId, className: section.className, sections: [section] });
        } else {
            paginatedGroups[paginatedGroups.length - 1].sections.push(section);
        }
    });

    return (
      <AdminPanelCard
        title="Class Timetables"
        subtitle="Open, edit, publish, and monitor timetable setup for each class and section."
        sx={{ mt: 0.5 }}
        contentSx={{ p: 0 }}
      >
        <TableContainer component={Box}>
          <Table sx={{ minWidth: 600 }}>
            <TableHead sx={{ bgcolor: "#F5F7FB" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: '20%' }}>Class</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: '20%' }}>Section</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: '20%' }} align="center">Setup Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: '20%' }} align="center">Publish Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: '20%' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedGroups.length === 0 && !initialLoading && (
                 <TableRow><TableCell colSpan={5} align="center" sx={{py: 3, fontStyle: 'italic', color: 'text.secondary'}}>No matching records found.</TableCell></TableRow>
              )}
              {paginatedGroups.map((cls) =>
                cls.sections.map((section, index) => {
                  const key = `${section.classId}-${section.sectionId}`;
                  const status = section.hasTimetable ? (section.timetableStatus || 'SAVED') : 'N/A';
                  const isPublishing = publishingMap[key] || false;

                  return (
                    <TableRow key={key} hover sx={{ '& td, & th': { borderBottom: '1px solid', borderColor: 'divider' }, '&:last-child td, &:last-child th': { borderBottom: 0 } }}>
                      {index === 0 && (
                          <TableCell rowSpan={cls.sections.length} sx={{ fontWeight: 700, bgcolor: '#F8FAFC', verticalAlign: 'top', pt: 2.2 }}>
                              {cls.className}
                          </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 500 }}>{section.sectionName}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          icon={section.hasTimetable ? <CheckCircleIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
                          label={section.hasTimetable ? 'Ready' : 'Pending'}
                          color={section.hasTimetable ? 'success' : 'default'}
                          variant={section.hasTimetable ? 'filled' : 'outlined'}
                          sx={{ borderRadius: 6, fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={status === 'N/A' ? 'Not Created' : status}
                          color={status === 'PUBLISHED' ? 'success' : status === 'SAVED' ? 'warning' : 'default'}
                          variant={status === 'N/A' ? 'outlined' : 'filled'}
                          sx={{ borderRadius: 6, fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title={section.hasTimetable ? "View/Edit Timetable" : "Create Timetable"}>
                            <IconButton size="small" color="primary" onClick={() => handleViewEditTimetable(section.classId, section.sectionId, section.className, section.sectionName)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {section.hasTimetable && (
                              <Tooltip title={status === 'SAVED' ? 'Publish Timetable' : 'Unpublish Timetable'}>
                                <span>
                                  <IconButton size="small" color={status === 'SAVED' ? 'success' : 'warning'} disabled={isPublishing} onClick={() => handleToggleStatus(section.classId, section.sectionId)}>
                                      {isPublishing ? <CircularProgress size={16} color="inherit" /> : (status === 'SAVED' ? <PublishIcon fontSize="small" /> : <UnpublishedIcon fontSize="small" />)}
                                  </IconButton>
                                </span>
                              </Tooltip>
                          )}
                          <Tooltip title="Auto-Generate Timetable (Coming Soon)">
                              <span><IconButton size="small" color="secondary" disabled><HourglassEmptyIcon fontSize="small" /></IconButton></span>
                          </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </AdminPanelCard>
    );
  };

  const renderTimetableDialog = () => {
     const classIdNum = Number(selectedClass);
     const sectionIdNum = Number(selectedSection);
     const currentTimetableData = timetables[classIdNum]?.[sectionIdNum] || {};

     const maxPeriodsInUse = daysOfWeek.reduce((maxP, day) => {
        const dayPeriods = currentTimetableData[day] || [];
        const lastUsedIndex = dayPeriods.map(p => !!p?.subjectId).lastIndexOf(true);
        return Math.max(maxP, lastUsedIndex + 1);
     }, MAX_PERIODS);

    return (
    <Dialog
      open={timetableDialogOpen}
      onClose={() => setTimetableDialogOpen(false)}
      maxWidth="xl"
      fullWidth
      TransitionComponent={FadeTransition}
      PaperProps={{
        sx: {
          borderRadius: 0,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 'bold', pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Timetable - {selectedClassName} / {selectedSectionName}</span>
        <IconButton onClick={() => setTimetableDialogOpen(false)} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1, sm: 3 }, bgcolor: 'background.default' }}>
        {dialogLoading ? (
          <AdminPanelCard title="Timetable Grid" subtitle="Loading period schedule, assigned subjects, and publish state.">
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '0.8fr repeat(5, 1fr)',
                  gap: 1,
                  px: 0.5,
                }}
              >
                {[...Array(6)].map((_, index) => (
                  <Skeleton key={index} variant="rounded" height={34} />
                ))}
              </Box>
              {[...Array(6)].map((_, rowIndex) => (
                <Box
                  key={rowIndex}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '0.8fr repeat(5, 1fr)',
                    gap: 1,
                    px: 0.5,
                  }}
                >
                  <Skeleton variant="rounded" height={52} />
                  {[...Array(5)].map((_, cellIndex) => (
                    <Skeleton key={cellIndex} variant="rounded" height={52} />
                  ))}
                </Box>
              ))}
            </Stack>
          </AdminPanelCard>
        ) : (
          <Box
            title="Weekly Grid"
            subtitle="Click any period cell to add or update the assigned subject and timing."
            contentSx={{ p: 0 }}
          >
          <TableContainer component={Box} ref={timetableRef}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead sx={{ bgcolor: "#F5F7FB" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: '10%' }}>Day</TableCell>
                  {[...Array(maxPeriodsInUse)].map((_, i) => (
                    <TableCell key={i} align="center" sx={{ fontWeight: 700, width: `${90 / maxPeriodsInUse}%` }}>
                      Period {i + 1}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {daysOfWeek.map((day) => {
                  const periods = currentTimetableData[day] || [];
                   while (periods.length < maxPeriodsInUse) periods.push({}); 
                  return (
                    <TableRow key={day} hover sx={{ '& td': { borderBottom: '1px solid', borderColor: 'divider' }, '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#F8FAFC' }}>{day}</TableCell>
                      {[...Array(maxPeriodsInUse)].map((_, i) => {
                        const entry = periods[i] || {};
                        const hasEntry = !!entry.subjectId;
                        return (
                          <StyledPeriodCell key={`${day}-${i}`} onClick={() => handleCellClick(day, i)} sx={{ border: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                            {hasEntry ? (
                                <Box sx={{display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center'}}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.dark', lineHeight: 1.2 }} align="center" noWrap>
                                    {entry.subjectName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" align="center" sx={{ display: 'block', mt: 0.5 }}>
                                      {formatTimeToAMPM(entry.startTime)} - {formatTimeToAMPM(entry.endTime)}
                                  </Typography>
                                </Box>
                            ) : (
                                <Box sx={{display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center'}}>
                                    <AddIcon fontSize="small" color="disabled" sx={{ opacity: 0.5 }} />
                                </Box>
                            )}
                          </StyledPeriodCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5, justifyContent: 'space-between' }}>
        <Button onClick={handleExportPDF} variant="outlined" startIcon={<PictureAsPdfIcon fontSize="small" />} disabled={dialogLoading || !Object.values(currentTimetableData).some(day => day?.some(p => p?.subjectId))}> Export PDF </Button>
        <Button onClick={() => setTimetableDialogOpen(false)} variant="contained" disableElevation> Close </Button>
      </DialogActions>
    </Dialog>
  )};

  const renderEditModal = () => {
    const { dayOfWeek, period } = currentEdit;
    if (dayOfWeek === '' || period === null) return null;

    const timetableEntry = timetables[selectedClass]?.[selectedSection]?.[dayOfWeek]?.[period] || {};
    const isNewEntry = !timetableEntry.timetableId;
    const formSubjectId = timetableEntry.subjectId ? String(timetableEntry.subjectId) : ''; 

    const availableSubjects = assignments
        .map(a => ({ subjectId: a.subjectId, subjectName: a.subjectName || 'Unnamed Subject' }))
        .filter((value, index, self) => index === self.findIndex((t) => t.subjectId === value.subjectId)) 
        .sort((a, b) => a.subjectName.localeCompare(b.subjectName)); 

    return (
      <Dialog
          open={editing}
          onClose={() => setEditing(false)}
          maxWidth="xs"
          fullWidth
          TransitionComponent={FadeTransition}
          PaperProps={{
            sx: {
              borderRadius: 0,
            },
          }}
      >
          <DialogTitle sx={{ fontWeight: 'bold' }}> {isNewEntry ? 'Add Period Entry' : 'Edit Period Entry'} </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}> {`Period ${period + 1} • ${dayOfWeek} • ${selectedClassName} / ${selectedSectionName}`} </Typography>
            <Box display="flex" flexDirection="column" gap={3}>
                <FormControl fullWidth required error={!formSubjectId && !isNewEntry}>
                    <InputLabel sx={{ bgcolor: "background.paper", px: 0.5 }}>Subject</InputLabel>
                    <Select
                        value={formSubjectId} 
                        disabled={assignmentLoading}
                        onChange={(e) => {
                            const val = e.target.value;
                            setTimetables((prev) => {
                                const newData = JSON.parse(JSON.stringify(prev));
                                if (!newData[selectedClass][selectedSection][dayOfWeek][period]) newData[selectedClass][selectedSection][dayOfWeek][period] = {};
                                newData[selectedClass][selectedSection][dayOfWeek][period].subjectId = Number(val);
                                newData[selectedClass][selectedSection][dayOfWeek][period].subjectName = assignments.find(s => s.subjectId === Number(val))?.subjectName;
                                return newData;
                            });
                        }}
                        label="Subject"
                    >
                        {assignmentLoading ? (
                            <MenuItem value="" disabled sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                Loading subjects...
                            </MenuItem>
                        ) : availableSubjects.length === 0 ? (
                            <MenuItem value="" disabled sx={{ fontStyle: 'italic', color: 'text.secondary' }}> No subjects assigned to this section </MenuItem>
                        ) : (
                            availableSubjects.map((sub) => <MenuItem key={sub.subjectId} value={String(sub.subjectId)}>{sub.subjectName}</MenuItem>)
                        )}
                    </Select>
                </FormControl>
                {!assignmentLoading && availableSubjects.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No subject assignments are available for this class and section yet.
                  </Typography>
                ) : null}
                
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                label="Start Time" type="time" fullWidth
                                value={timetableEntry.startTime || ''}
                            onChange={(e) => setTimetables(prev => {
                                const newData = JSON.parse(JSON.stringify(prev));
                                newData[selectedClass][selectedSection][dayOfWeek][period].startTime = e.target.value;
                                return newData;
                            })}
                            InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }} required
                        />
                    </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="End Time" type="time" fullWidth
                                value={timetableEntry.endTime || ''}
                            onChange={(e) => setTimetables(prev => {
                                const newData = JSON.parse(JSON.stringify(prev));
                                newData[selectedClass][selectedSection][dayOfWeek][period].endTime = e.target.value;
                                return newData;
                            })}
                            InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }} required
                                error={!!timetableEntry.startTime && !!timetableEntry.endTime && timetableEntry.startTime >= timetableEntry.endTime}
                            />
                        </Grid>
                    </Grid>
                    <TextField
                        label="Google Meet Link (optional)"
                        fullWidth
                        value={timetableEntry.meetLink || ''}
                        onChange={(e) => setTimetables(prev => {
                            const newData = JSON.parse(JSON.stringify(prev));
                            if (!newData[selectedClass][selectedSection][dayOfWeek][period]) newData[selectedClass][selectedSection][dayOfWeek][period] = {};
                            newData[selectedClass][selectedSection][dayOfWeek][period].meetLink = e.target.value;
                            return newData;
                        })}
                        placeholder="https://meet.google.com/xxx-xxxx-xxx"
                    />
                    {timetableEntry.meetLink ? (
                        <Button
                            variant="outlined"
                            onClick={() => window.open(timetableEntry.meetLink, '_blank', 'noopener,noreferrer')}
                        >
                            Open Meet
                        </Button>
                    ) : null}
                </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setEditing(false)} color="inherit" disabled={isSaving}> Cancel </Button>
            <Button variant="contained" disableElevation color="primary" onClick={handleSaveTimetable} disabled={ isSaving || availableSubjects.length === 0 || !formSubjectId || !timetableEntry.startTime || !timetableEntry.endTime || (!!timetableEntry.startTime && !!timetableEntry.endTime && timetableEntry.startTime >= timetableEntry.endTime) } >
              {isSaving ? <CircularProgress size={24} color="inherit" /> : (isNewEntry ? 'Add Entry' : 'Update Entry')}
            </Button>
          </DialogActions>
      </Dialog>
    );
  };

  return (
    <ThemeProvider theme={adminTheme}>
        <CssBaseline />
        <PageContainer sx={{ width: '100%' }}>
            <AdminPageHero
              title="Timetable Management"
              subtitle="Plan, review, and publish class schedules with a cleaner weekly timetable workflow."
              metrics={timetableMetrics}
            />

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.4} mt={2} flexWrap="wrap" gap={2}>
                <TextField
                    placeholder="Search classes or sections..."
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ flexGrow: 1, maxWidth: 400, "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
                />
            </Box>

            {initialLoading && <TableSkeleton rows={8} />}

            {!initialLoading && error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
            )}

            {!initialLoading && !error && renderClassesSections()}

            {timetableDialogOpen && renderTimetableDialog()}
            {editing && renderEditModal()}

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: '100%', color: '#fff', fontWeight: 500, borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </PageContainer>
    </ThemeProvider>
  );
};

export default TimetableComponent;
